import { logger } from '@/utils/logger.js';

// Import types
import type {
  EffectElements,
  EffectCatalog,
  ColorPalettes,
  BackgroundState,
  FlashControl,
  Mood,
  MusicAnalysis,
  EffectType
} from './visual-beat-effects/types.js';

// Import modules
import {
  findTargetContainer as _findTargetContainer,
  initializeEffectElements,
  ensureElementExists as ensureElement,
  createParticleContainer
} from './visual-beat-effects/effect-elements.js';

import {
  createParticle as _createParticle,
  initializeParticlePool,
  animateParticle as animateParticleImpl,
  returnParticleToPool as returnParticle,
  triggerParticleExplosion as triggerParticleExplosionImpl,
  animateSparkleParticle as animateSparkle,
  triggerSubtleSparkle as triggerSparkle
} from './visual-beat-effects/particle-system.js';

import {
  triggerScreenFlash as triggerFlash,
  updateBackgroundMood as updateMood,
  triggerShockwave as triggerWave,
  applyCameraShake as applyShake,
  triggerSharpFlash as triggerSharp,
  triggerColorStrobe as triggerStrobe,
  getMoodFilters as _getMoodFilters
} from './visual-beat-effects/visual-effects.js';

import {
  onKickDetected as onKick,
  onSnareDetected as onSnare,
  onHihatDetected as onHihat,
  triggerExplosionEffect,
  triggerElectricalEffect as triggerElectrical,
  triggerComplexityEffect as triggerComplexity
} from './visual-beat-effects/beat-handlers.js';

import {
  initializeEffectCatalog,
  useFallbackCatalog as _useFallbackCatalog
} from './visual-beat-effects/effect-catalog.js';

/**
 * 背景・視覚エフェクト用BGMビート連動演出システム
 * 画面全体の視覚的インパクトとサイネージ効果
 */
class VisualBeatEffects {
  private isActive: boolean;
  private effectElements: EffectElements;
  private particlePool: HTMLElement[];
  public maxParticles: number;
  private activeParticles: HTMLElement[];
  private effectCatalog: EffectCatalog;
  private colorPalettes: ColorPalettes;
  private backgroundState: BackgroundState;
  private flashControl: FlashControl;
  /** インスタンス識別用マーカー（デバッグ用） */
  public _instanceMarker: string;
  /** インスタンスカウンター */
  private static instanceCount = 0;

  constructor() {
    VisualBeatEffects.instanceCount++;
    this._instanceMarker = `VBE_${VisualBeatEffects.instanceCount}_${Date.now()}`;
    logger.info(`🎨 VisualBeatEffects constructor - instance: ${this._instanceMarker}`);
    this.isActive = false;

    // Effect elements management
    this.effectElements = {
      screenFlash: null,
      particles: null,
      background: null,
      overlay: null,
      shockwave: null
    };

    // Particle pool
    this.particlePool = [];
    this.maxParticles = 50;
    this.activeParticles = [];

    // Effect catalog
    this.effectCatalog = {
      kick: [],
      snare: [],
      hihat: []
    };
    initializeEffectCatalog(this.effectCatalog);

    // Color palettes (Cyberpunk theme - unified for all moods)
    // 🎨 サイバーパンク風カラー: ネオンマゼンタ、シアン、パープル、ホットピンク
    const cyberpunkColors = ['#ff00ff', '#00ffff', '#bf00ff', '#ff0080', '#00ff80', '#8000ff'];
    this.colorPalettes = {
      intense: cyberpunkColors,
      energetic: cyberpunkColors,
      normal: cyberpunkColors,
      chill: cyberpunkColors
    };

    // Background effect state
    this.backgroundState = {
      currentGradient: 'normal',
      lastTransition: 0,
      isAnimating: false
    };

    // Screen flash control
    this.flashControl = {
      lastFlash: 0,
      minInterval: 50,
      maxIntensity: 0.8
    };

    // Initialize when DOM is ready
    this.initializeWhenReady();
  }

  /**
   * Initialize after DOM ready
   */
  initializeWhenReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      setTimeout(() => this.initialize(), 1000);
    }
  }

  /**
   * Actual initialization
   */
  initialize() {
    const baseMaxRetries = 5;
    let retryCount = 0;

    const isCharacterOnlyMode = document.body.classList.contains('character-only-mode');
    const maxRetries = isCharacterOnlyMode ? 60 : baseMaxRetries;

    const attemptInitialization = () => {
      try {
        const characterPanel = document.querySelector('.character-only-panel');
        const demoContainer = document.querySelector('.demo-container');

        if (!characterPanel) {
          if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(attemptInitialization, 500);
            return;
          }

          if (isCharacterOnlyMode) {
            logger.warn('⚠️ character-only-mode active but character panel not found after retries; waiting for availability');
            retryCount = 0;
            setTimeout(attemptInitialization, 500);
            return;
          }

          if (!demoContainer) {
            logger.warn('⚠️ Demo container not found, retrying...');
            retryCount = 0;
            setTimeout(attemptInitialization, 500);
            return;
          }
        }

        initializeEffectElements(this.effectElements);
        this.initializeParticleSystem();
      } catch (error) {
        logger.error('❌ Visual Beat Effects initialization error:', error);
        if (retryCount < maxRetries) {
          retryCount++;
          setTimeout(attemptInitialization, 500);
        }
      }
    };

    attemptInitialization();
  }

  /**
   * Initialize particle system
   * パーティクルコンテナがない場合は自動作成
   */
  initializeParticleSystem() {
    logger.info(`🎨 initializeParticleSystem called - current poolSize: ${this.particlePool.length}, maxParticles: ${this.maxParticles}, this instance: ${(this as unknown as { _instanceMarker?: string })._instanceMarker || 'unmarked'}`);

    // パーティクルコンテナがなければ作成
    if (!this.effectElements.particles) {
      logger.info('🎨 Creating particle container...');
      createParticleContainer(this.effectElements);

      if (!this.effectElements.particles) {
        logger.warn('⚠️ Failed to create particle container');
        return;
      }
      logger.info('✅ Particle container created');
    }

    // 配列の参照を保持しつつクリア（新しい配列を代入しない）
    this.particlePool.length = 0;
    this.activeParticles.length = 0;

    logger.info(`🎨 Initializing particle pool with maxParticles: ${this.maxParticles}`);
    initializeParticlePool(this.effectElements, this.particlePool, this.maxParticles);

    logger.info(`✅ Particle pool initialized with ${this.particlePool.length} particles`);
  }

  /**
   * Ensure element exists (lazy initialization)
   */
  ensureElementExists(elementType: keyof EffectElements): void {
    ensureElement(
      elementType,
      this.effectElements,
      this.particlePool,
      this.maxParticles,
      () => this.initializeParticleSystem()
    );
  }

  /**
   * Kick beat effect handler
   */
  onKickDetected(intensity: number, mood: Mood, musicAnalysis: MusicAnalysis, effectType: EffectType = 'all') {
    logger.info(`🥁 VisualBeatEffects.onKickDetected called - instance: ${this._instanceMarker}, intensity: ${intensity.toFixed(2)}, mood: ${mood}, isActive: ${this.isActive}, poolSize: ${this.particlePool.length}`);
    if (!this.isActive) {
      logger.warn('⚠️ VisualBeatEffects is NOT active - skipping effect');
      return;
    }
    onKick(
      intensity,
      mood,
      musicAnalysis,
      effectType,
      this.isActive,
      (i, m) => this.triggerScreenFlash(i, m),
      (i, m) => this.triggerParticleExplosion(i, m),
      (m, i) => this.updateBackgroundMood(m, i),
      (i) => this.triggerShockwave(i),
      (i, m) => this.applyCameraShake(i, m),
      (i, m) => this.triggerExplosionEffectWrapper(i, m),
      (i, m) => this.triggerColorStrobe(i, m),
      (ma, m) => this.triggerComplexityEffect(ma, m)
    );
  }

  /**
   * Snare beat effect handler
   */
  onSnareDetected(intensity: number, mood: Mood, effectType: EffectType = 'all') {
    onSnare(
      intensity,
      mood,
      effectType,
      this.isActive,
      (i, m) => this.triggerSharpFlash(i, m),
      (i) => this.triggerElectricalEffect(i)
    );
  }

  /**
   * Hihat beat effect handler
   */
  onHihatDetected(intensity: number, _mood: Mood, _effectType: EffectType = 'all') {
    onHihat(
      intensity,
      _mood,
      _effectType,
      this.isActive,
      (i) => this.triggerSubtleSparkle(i)
    );
  }

  /**
   * Screen flash
   */
  triggerScreenFlash(intensity: number, mood: Mood) {
    triggerFlash(
      intensity,
      mood,
      this.effectElements,
      this.colorPalettes,
      this.flashControl,
      (et) => this.ensureElementExists(et)
    );
  }

  /**
   * Particle explosion
   */
  triggerParticleExplosion(intensity: number, mood: Mood) {
    logger.debug(`💥 triggerParticleExplosion called - intensity: ${intensity.toFixed(2)}, mood: ${mood}, poolSize: ${this.particlePool.length}, maxParticles: ${this.maxParticles}, container: ${!!this.effectElements.particles}, isActive: ${this.isActive}`);
    triggerParticleExplosionImpl(
      intensity,
      mood,
      this.effectElements,
      this.particlePool,
      this.activeParticles,
      this.colorPalettes,
      (p, i, c) => this.animateParticle(p, i, c),
      () => this.initializeParticleSystem()
    );
  }

  /**
   * Animate particle
   */
  animateParticle(particle: HTMLElement, intensity: number, colors: string[]) {
    animateParticleImpl(
      particle,
      intensity,
      colors,
      this.effectElements,
      (p) => this.returnParticleToPool(p)
    );
  }

  /**
   * Return particle to pool
   */
  returnParticleToPool(particle: HTMLElement) {
    returnParticle(particle, this.activeParticles, this.particlePool);
  }

  /**
   * Update background mood
   */
  updateBackgroundMood(mood: Mood, intensity: number) {
    updateMood(
      mood,
      intensity,
      this.effectElements,
      this.colorPalettes,
      this.backgroundState,
      (et) => this.ensureElementExists(et)
    );
  }

  /**
   * Trigger shockwave
   */
  triggerShockwave(intensity: number) {
    triggerWave(
      intensity,
      this.effectElements,
      (et) => this.ensureElementExists(et)
    );
  }

  /**
   * Apply camera shake
   */
  applyCameraShake(intensity: number, mood: Mood) {
    logger.debug(`📳 applyCameraShake called - intensity: ${intensity.toFixed(2)}, mood: ${mood}`);
    applyShake(intensity, mood);
  }

  /**
   * Sharp flash (snare)
   */
  triggerSharpFlash(intensity: number, mood: Mood) {
    triggerSharp(
      intensity,
      mood,
      this.effectElements,
      this.colorPalettes,
      (et) => this.ensureElementExists(et)
    );
  }

  /**
   * Explosion effect (wrapper)
   */
  triggerExplosionEffectWrapper(intensity: number, mood: Mood) {
    triggerExplosionEffect(intensity, mood, (i: number, m: Mood) => this.triggerParticleExplosion(i, m));
  }

  /**
   * Electrical effect
   */
  triggerElectricalEffect(intensity: number) {
    triggerElectrical(intensity, (i, m) => this.triggerScreenFlash(i, m));
  }

  /**
   * Color strobe
   */
  triggerColorStrobe(intensity: number, mood: Mood) {
    triggerStrobe(intensity, mood, this.effectElements, this.colorPalettes);
  }

  /**
   * Complexity effect
   */
  triggerComplexityEffect(musicAnalysis: MusicAnalysis, mood: Mood) {
    triggerComplexity(musicAnalysis, mood, (i, m) => this.triggerParticleExplosion(i, m));
  }

  /**
   * Subtle sparkle (hihat)
   */
  triggerSubtleSparkle(intensity: number) {
    triggerSparkle(
      intensity,
      this.particlePool,
      this.activeParticles,
      this.effectElements,
      (p, i) => this.animateSparkleParticle(p, i)
    );
  }

  /**
   * Animate sparkle particle
   */
  animateSparkleParticle(particle: HTMLElement, intensity: number) {
    animateSparkle(
      particle,
      intensity,
      this.effectElements,
      (p) => this.returnParticleToPool(p)
    );
  }

  /**
   * Activate effect system
   */
  activate() {
    this.isActive = true;
    logger.info('✅ VisualBeatEffects ACTIVATED - isActive set to true');
    this.performHealthCheck();

    setTimeout(() => {
      this.performHealthCheck();
      logger.debug(`🎨 VisualBeatEffects health check complete - isActive: ${this.isActive}, particles: ${this.particlePool.length}`);
    }, 500);
  }

  /**
   * System health check
   */
  performHealthCheck() {
    const elements = ['screenFlash', 'background', 'particles', 'shockwave'];
    const missing: string[] = [];

    for (const element of elements) {
      if (!this.effectElements[element]) {
        missing.push(element);
      }
    }

    if (this.particlePool.length === 0) {
      missing.push('particlePool');
    }

    if (missing.length > 0) {
      this.initialize();
    }
  }

  /**
   * Deactivate effect system
   */
  deactivate() {
    this.isActive = false;
  }

  /**
   * Cleanup system
   */
  dispose() {
    this.deactivate();

    Object.values(this.effectElements).forEach(element => {
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
    });
  }
}

// Global export
if (typeof window !== 'undefined') {
  window.VisualBeatEffects = VisualBeatEffects;
  logger.info('🎨 VisualBeatEffects module loaded and registered to window');
}

// ES module export
export { VisualBeatEffects };
